import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import {
  ArrowLeftIcon,
  CalendarDaysIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  XMarkIcon,
} from '@heroicons/react/24/solid';

import { useAuth } from '../context/AuthContext';
import api from '../utils/api';

const endpoint = '/api/audit/reports/admin-collections-calendar/';
const minDate = '2026-02-10';
const minMonth = { year: 2026, month: 2 };
const minMonthValue = '2026-02';
const weekDays = ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'];
const monthNames = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];

const money = (value) => `$${Number(value || 0).toFixed(2)}`;
const countOf = (group) => Number(group?.count || 0);
const zeroGroup = (paymentType = 'all') => {
  const cashApplicable = paymentType !== 'credit';
  const creditApplicable = paymentType !== 'cash';
  const amount = (applicable) => ({ applicable, count: 0, total: 0 });
  return {
    rojo: { count: 0, total: 0, cash: amount(cashApplicable), credit: amount(creditApplicable) },
    amarillo: { count: 0, total: 0, cash: amount(cashApplicable), credit: amount(creditApplicable) },
    creditos_por_cobrar: amount(creditApplicable),
    extras_no_pagados: amount(cashApplicable),
  };
};

const isBeforeMinimumMonth = ({ year, month }) => (
  year < minMonth.year || (year === minMonth.year && month < minMonth.month)
);

const shiftMonth = ({ year, month }, direction) => {
  const date = new Date(year, month - 1 + direction, 1);
  return { year: date.getFullYear(), month: date.getMonth() + 1 };
};

const parseMonthValue = (value) => {
  const match = /^(\d{4})-(\d{2})$/.exec(value);
  if (!match) return null;

  const selectedMonth = { year: Number(match[1]), month: Number(match[2]) };
  if (selectedMonth.month < 1 || selectedMonth.month > 12 || isBeforeMinimumMonth(selectedMonth)) {
    return null;
  }
  return selectedMonth;
};

const formatDayLabel = (year, month, day) => `${day} de ${monthNames[month - 1].toLowerCase()} de ${year}`;

const focusableSelector = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

const Signal = ({ color, label, count }) => (
  <span
    role="img"
    aria-label={`${label}: ${count}`}
    title={`${label}: ${count}`}
    className="inline-flex min-w-0 items-center gap-1 text-[10px] font-bold leading-none tabular-nums text-gray-100"
  >
    <span aria-hidden="true" className={`h-2.5 w-2.5 shrink-0 rounded-full ${color}`} />
    <span aria-hidden="true">{count}</span>
  </span>
);

const AmountCard = ({ label, group, tone, showNotApplicable = false }) => {
  const unavailable = showNotApplicable && group?.applicable === false;
  return (
    <section aria-label={label} className="min-w-0 rounded-lg bg-notaria-blue-1/80 p-3 shadow-lg ring-1 ring-white/5 sm:p-4">
      <p className="min-h-[1.75rem] break-words text-[11px] font-semibold uppercase leading-tight text-gray-300">{label}</p>
      <p className={`mt-1 break-words text-xl font-bold ${tone}`}>{unavailable ? 'No aplica' : money(group?.total)}</p>
      <p className="mt-1 break-words text-xs leading-tight text-gray-300">{unavailable ? 'No disponible en este modo' : `${countOf(group)} registros`}</p>
    </section>
  );
};

const CalendarDay = ({ year, month, day, entry, onOpen }) => {
  const date = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
  const disabled = date < minDate;
  const totals = entry?.totals || zeroGroup();
  const redCash = countOf(totals.rojo?.cash);
  const yellow = countOf(totals.amarillo);
  const credits = countOf(totals.creditos_por_cobrar);
  const extras = countOf(totals.extras_no_pagados);
  const active = Boolean(entry?.has_activity || redCash || yellow || credits || extras);
  const dayLabel = formatDayLabel(year, month, day);
  const details = disabled
    ? `${dayLabel}, no disponible`
    : active
      ? `${dayLabel}, contado por justificar: ${redCash}, pendientes de aprobación: ${yellow}, créditos por cobrar: ${credits}, extras: ${extras}`
      : `${dayLabel}, sin actividad`;

  const openFromKeyboard = (event) => {
    if ((event.key === 'Enter' || event.key === ' ') && !disabled) {
      event.preventDefault();
      onOpen(entry || { date, day, totals, matrizadores: [] }, event.currentTarget);
    }
  };

  return (
    <button
      type="button"
      disabled={disabled}
      aria-label={details}
      onClick={(event) => !disabled && onOpen(entry || { date, day, totals, matrizadores: [] }, event.currentTarget)}
      onKeyDown={openFromKeyboard}
      className={`min-h-[6rem] w-full rounded-md p-2 text-left transition duration-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-notaria-yellow sm:min-h-[5.25rem] ${
        disabled
          ? 'cursor-not-allowed bg-notaria-blue-2/20 text-gray-600'
          : active
            ? 'bg-notaria-blue-2/80 text-white hover:bg-notaria-blue-3 active:scale-[0.98]'
            : 'bg-white/5 text-gray-300 hover:bg-white/10 active:scale-[0.98]'
      }`}
    >
      <span className="block text-sm font-bold">{day}</span>
      {active && !disabled && (
        <span className="mt-1 grid grid-cols-1 justify-items-start gap-0.5 sm:mt-2 sm:flex sm:flex-wrap sm:items-center sm:gap-x-2 sm:gap-y-1">
          {redCash > 0 && <Signal color="bg-red-500" label="Contado por justificar" count={redCash} />}
          {yellow > 0 && <Signal color="bg-amber-400" label="Pendientes de aprobación" count={yellow} />}
          {credits > 0 && <Signal color="bg-sky-400" label="Créditos por cobrar" count={credits} />}
          {extras > 0 && <Signal color="bg-fuchsia-400" label="Extras" count={extras} />}
        </span>
      )}
    </button>
  );
};

const splitValue = (group) => (
  group?.applicable === false ? 'No aplica' : `${countOf(group)} / ${money(group?.total)}`
);

const Split = ({ group, cashLabel = 'Efectivo', creditLabel = 'Crédito' }) => (
  <dl className="mt-2 grid grid-cols-2 gap-2 text-xs text-gray-300">
    <div><dt>{cashLabel}</dt><dd className="font-semibold text-white">{splitValue(group?.cash)}</dd></div>
    <div><dt>{creditLabel}</dt><dd className="font-semibold text-white">{splitValue(group?.credit)}</dd></div>
  </dl>
);

const DrawerGroup = ({ title, matrizadores, groupFor, unavailable = false, renderDetails }) => {
  const applicableMatrizadores = matrizadores.filter((matrizador) => (
    countOf(groupFor(matrizador)) > 0
  ));
  return (
    <section className="border-t border-white/10 py-4 first:border-t-0 first:pt-0">
      <h3 className="text-base font-bold text-white">{title}</h3>
      <div className="mt-3 space-y-3">
        {unavailable ? (
          <p className="text-sm text-gray-300">No aplica para este tipo de pago.</p>
        ) : applicableMatrizadores.length === 0 ? (
          <p className="text-sm text-gray-400">Sin registros en esta categoría.</p>
        ) : applicableMatrizadores.map((matrizador, index) => {
          const group = groupFor(matrizador);
          return (
            <div key={`${matrizador?.name || 'matrizador'}-${index}`} className="rounded-md bg-notaria-blue-2/60 p-3">
              <p className="text-sm font-bold tracking-wide text-white">{String(matrizador?.name || 'Sin matrizador').toUpperCase()}</p>
              {renderDetails(group)}
            </div>
          );
        })}
      </div>
    </section>
  );
};

const DayDrawer = ({ day, onClose, reducedMotion }) => {
  const drawerRef = useRef(null);
  const closeRef = useRef(null);
  const totals = day?.totals || zeroGroup();
  const matrizadores = Array.isArray(day?.matrizadores) ? day.matrizadores : [];

  useEffect(() => {
    closeRef.current?.focus();

    const trapFocus = (event) => {
      if (event.key !== 'Tab') return;

      const drawer = drawerRef.current;
      if (!drawer) return;
      const focusableElements = Array.from(drawer.querySelectorAll(focusableSelector)).filter((element) => (
        element.tabIndex >= 0
        && element.getAttribute('aria-hidden') !== 'true'
        && !element.closest('[hidden]')
      ));

      if (focusableElements.length === 0) {
        event.preventDefault();
        drawer.focus();
        return;
      }

      const first = focusableElements[0];
      const last = focusableElements[focusableElements.length - 1];
      const activeElement = document.activeElement;

      if (!drawer.contains(activeElement)) {
        event.preventDefault();
        (event.shiftKey ? last : first).focus();
      } else if (event.shiftKey && activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', trapFocus);
    return () => document.removeEventListener('keydown', trapFocus);
  }, []);

  const title = `Detalle del ${formatDayLabel(new Date(`${day.date}T00:00:00`).getFullYear(), new Date(`${day.date}T00:00:00`).getMonth() + 1, day.day || new Date(`${day.date}T00:00:00`).getDate())}`;
  const initial = reducedMotion ? false : { x: '100%', opacity: 0 };
  const animate = { x: 0, opacity: 1 };

  return (
    <motion.aside
      ref={drawerRef}
      role="dialog"
      aria-modal="true"
      aria-label={title}
      tabIndex={-1}
      initial={initial}
      animate={animate}
      exit={reducedMotion ? { opacity: 0 } : { x: '100%', opacity: 0 }}
      transition={{ type: 'spring', stiffness: 300, damping: 30 }}
      className="fixed inset-0 z-40 flex w-full flex-col overflow-y-auto bg-gray-900 p-5 text-white shadow-2xl sm:inset-y-0 sm:right-0 sm:left-auto sm:max-w-[30rem]"
    >
      <div className="mb-5 flex items-start justify-between gap-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-notaria-yellow">Cumplimiento diario</p>
          <h2 className="mt-1 text-xl font-bold">{title}</h2>
        </div>
        <button ref={closeRef} type="button" onClick={onClose} aria-label="Cerrar detalle" className="rounded-md p-2 text-white hover:bg-white/10 focus-visible:outline focus-visible:outline-2 focus-visible:outline-notaria-yellow">
          <XMarkIcon className="h-6 w-6" />
        </button>
      </div>

      <DrawerGroup
        title="Contado por justificar"
        matrizadores={matrizadores}
        groupFor={(matrizador) => matrizador?.rojo?.cash}
        unavailable={totals.rojo?.cash?.applicable === false}
        renderDetails={(group) => (
          <><p className="mt-1 text-sm text-gray-200">{countOf(group)} registros</p><strong className="block text-base text-red-200">{money(group?.total)}</strong></>
        )}
      />
      <DrawerGroup
        title="Pendientes de aprobación"
        matrizadores={matrizadores}
        groupFor={(matrizador) => matrizador?.amarillo}
        renderDetails={(group) => (
          <><p className="mt-1 text-xs text-gray-300">Clasificación operativa del semáforo amarillo.</p><Split group={group} cashLabel="Contado operativo" creditLabel="Crédito operativo" /></>
        )}
      />
      <DrawerGroup
        title="Créditos por cobrar"
        matrizadores={matrizadores}
        groupFor={(matrizador) => matrizador?.creditos_por_cobrar}
        unavailable={totals.creditos_por_cobrar?.applicable === false}
        renderDetails={(group) => (
          <><p className="mt-1 text-xs text-gray-300">Saldo por estado pendiente de cobro.</p><p className="mt-1 text-sm text-gray-200">{countOf(group)} registros</p><strong className="block text-base text-sky-200">{money(group?.total)}</strong></>
        )}
      />
      <DrawerGroup
        title="Extras no pagados"
        matrizadores={matrizadores}
        groupFor={(matrizador) => matrizador?.extras_no_pagados}
        unavailable={totals.extras_no_pagados?.applicable === false}
        renderDetails={(group) => (
          <><p className="mt-1 text-sm text-gray-200">{countOf(group)} registros</p><strong className="block text-base text-fuchsia-200">{money(group?.total)}</strong></>
        )}
      />
    </motion.aside>
  );
};

const AdminCollectionsCalendar = () => {
  const { user, loading } = useAuth() || { user: null, loading: true };
  const hasCollectionsPermission = user?.role === 'notario' || user?.is_superuser;
  const now = new Date();
  const todayMonth = { year: now.getFullYear(), month: now.getMonth() + 1 };
  const initialMonth = isBeforeMinimumMonth(todayMonth) ? minMonth : todayMonth;
  const [month, setMonth] = useState(initialMonth);
  const [paymentType, setPaymentType] = useState('all');
  const [matrizador, setMatrizador] = useState('');
  const [matrizadores, setMatrizadores] = useState([]);
  const [calendar, setCalendar] = useState(null);
  const [status, setStatus] = useState('loading');
  const [selectedDay, setSelectedDay] = useState(null);
  const [retry, setRetry] = useState(0);
  const requestRef = useRef(0);
  const abortRef = useRef(null);
  const openerRef = useRef(null);
  const reducedMotion = useReducedMotion();

  const params = useMemo(() => {
    const value = { year: month.year, month: month.month, payment_type: paymentType };
    if (matrizador) value.usuario_matrizador = matrizador;
    return value;
  }, [month, paymentType, matrizador]);

  useEffect(() => {
    if (!hasCollectionsPermission) return undefined;
    const controller = new AbortController();
    abortRef.current?.abort();
    abortRef.current = controller;
    const requestId = requestRef.current + 1;
    requestRef.current = requestId;
    setStatus('loading');
    setCalendar(null);

    api.get(endpoint, { params, signal: controller.signal })
      .then((response) => {
        if (requestRef.current !== requestId || controller.signal.aborted) return;
        setCalendar(response.data || null);
        setStatus('ready');
      })
      .catch((error) => {
        if (requestRef.current !== requestId || controller.signal.aborted) return;
        setCalendar(null);
        setStatus('error');
      });

    return () => controller.abort();
  }, [hasCollectionsPermission, params, retry]);

  useEffect(() => {
    if (!hasCollectionsPermission) return;
    let alive = true;
    api.get('/api/audit/matrizadores/list/')
      .then((response) => {
        if (!alive) return;
        setMatrizadores(Array.isArray(response.data) ? response.data : []);
      })
      .catch(() => alive && setMatrizadores([]));
    return () => { alive = false; };
  }, [hasCollectionsPermission]);

  const closeDrawer = useCallback(() => {
    setSelectedDay(null);
    window.setTimeout(() => openerRef.current?.focus(), 0);
  }, []);

  useEffect(() => {
    if (!selectedDay) return undefined;
    const priorOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKeyDown = (event) => {
      if (event.key === 'Escape') closeDrawer();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.body.style.overflow = priorOverflow;
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [selectedDay, closeDrawer]);

  const openDay = useCallback((day, opener) => {
    openerRef.current = opener;
    setSelectedDay(day);
  }, []);

  const daysByDate = useMemo(() => new Map((calendar?.days || []).map((day) => [day.date, day])), [calendar]);
  const startOffset = new Date(month.year, month.month - 1, 1).getDay();
  const mondayOffset = (startOffset + 6) % 7;
  const daysInMonth = new Date(month.year, month.month, 0).getDate();
  const totalSlots = Math.ceil((mondayOffset + daysInMonth) / 7) * 7;
  const totals = calendar?.totals || zeroGroup(paymentType);
  const canGoBack = !isBeforeMinimumMonth(shiftMonth(month, -1));
  const isEmpty = status === 'ready' && (calendar?.days || []).every((day) => !day.has_activity);
  const monthValue = `${month.year}-${String(month.month).padStart(2, '0')}`;

  const handleMonthChange = (event) => {
    const selectedMonth = parseMonthValue(event.target.value);
    if (selectedMonth) setMonth(selectedMonth);
  };

  if (loading) return <div className="min-h-screen bg-gray-900 p-6 text-white">Cargando...</div>;
  if (!hasCollectionsPermission) return null;

  return (
    <motion.div initial={reducedMotion ? false : { opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.28 }}>
      <main className="min-h-screen bg-gray-900 p-4 text-white sm:p-6">
      <div className="mx-auto max-w-7xl">
        <header className="mb-6 flex flex-wrap items-center justify-between gap-4">
          <div>
            <Link to="/admin-cobros" className="inline-flex items-center gap-2 text-sm font-semibold text-gray-300 hover:text-notaria-yellow focus-visible:outline focus-visible:outline-2 focus-visible:outline-notaria-yellow">
              <ArrowLeftIcon className="h-4 w-4" /> Volver a cobros
            </Link>
            <div className="mt-3 flex items-center gap-3">
              <CalendarDaysIcon className="h-7 w-7 text-notaria-yellow" />
              <div><h1 className="text-2xl font-bold sm:text-3xl">Calendario de cumplimiento</h1><p className="text-sm text-gray-300">{monthNames[month.month - 1]} {month.year}</p></div>
            </div>
          </div>
          <div className="flex w-full flex-wrap items-end gap-2 sm:w-auto">
            <label className="flex w-full min-w-0 flex-col gap-1 text-sm font-semibold text-gray-200 sm:w-auto">
              Mes del calendario
              <input
                type="month"
                min={minMonthValue}
                value={monthValue}
                onChange={handleMonthChange}
                className="min-w-0 rounded-md bg-notaria-blue-2 px-3 py-2 text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-notaria-yellow"
              />
            </label>
            <div className="flex shrink-0 items-center gap-2">
              <button type="button" aria-label="Mes anterior" disabled={!canGoBack} onClick={() => setMonth((current) => shiftMonth(current, -1))} className="rounded-md bg-notaria-blue-2 p-2 hover:bg-notaria-blue-3 disabled:cursor-not-allowed disabled:opacity-40 focus-visible:outline focus-visible:outline-2 focus-visible:outline-notaria-yellow"><ChevronLeftIcon className="h-5 w-5" /></button>
              <button type="button" onClick={() => setMonth(initialMonth)} className="rounded-md bg-notaria-blue-2 px-4 py-2 text-sm font-semibold hover:bg-notaria-blue-3 focus-visible:outline focus-visible:outline-2 focus-visible:outline-notaria-yellow">Hoy</button>
              <button type="button" aria-label="Mes siguiente" onClick={() => setMonth((current) => shiftMonth(current, 1))} className="rounded-md bg-notaria-blue-2 p-2 hover:bg-notaria-blue-3 focus-visible:outline focus-visible:outline-2 focus-visible:outline-notaria-yellow"><ChevronRightIcon className="h-5 w-5" /></button>
            </div>
          </div>
        </header>

        <section className="mb-5 flex flex-col gap-3 rounded-lg bg-notaria-blue-1/80 p-4 ring-1 ring-white/5 lg:flex-row lg:items-center lg:justify-between">
          <label className="flex min-w-0 flex-col gap-1 text-sm font-semibold text-gray-200">Matrizador
            <select aria-label="Matrizador" value={matrizador} onChange={(event) => setMatrizador(event.target.value)} className="min-w-0 rounded-md bg-notaria-blue-2 px-3 py-2 text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-notaria-yellow">
              <option value="">Todos los matrizadores</option>
              {matrizadores.map((item) => <option key={item.id || item.nombre} value={item.nombre}>{item.nombre}</option>)}
            </select>
          </label>
          <div role="group" aria-label="Tipo de pago" className="flex rounded-md bg-notaria-blue-2 p-1">
            {[['all', 'Todos'], ['cash', 'Efectivo'], ['credit', 'Crédito']].map(([value, label]) => <button key={value} type="button" aria-pressed={paymentType === value} onClick={() => setPaymentType(value)} className={`min-w-0 flex-1 rounded px-3 py-2 text-sm font-semibold transition duration-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-notaria-yellow ${paymentType === value ? 'bg-notaria-yellow text-notaria-blue-1' : 'text-white hover:bg-notaria-blue-3'}`}>{label}</button>)}
          </div>
        </section>

        <section aria-label="Totales financieros del calendario" className="mb-5 grid grid-cols-1 gap-2 sm:grid-cols-3 sm:gap-3">
          <AmountCard label="Total rojos al contado por justificar" group={totals.rojo?.cash} tone="text-red-300" showNotApplicable />
          <AmountCard label="Total créditos por cobrar" group={totals.creditos_por_cobrar} tone="text-sky-200" showNotApplicable />
          <AmountCard label="Extras no pagados" group={totals.extras_no_pagados} tone="text-fuchsia-200" showNotApplicable />
        </section>

        {status === 'loading' && <section role="status" aria-label="Cargando calendario" className="grid grid-cols-7 gap-1 rounded-lg bg-notaria-blue-1/80 p-3"><span className="sr-only">Cargando calendario</span>{Array.from({ length: 35 }, (_, index) => <span key={index} className="h-20 animate-pulse rounded-md bg-notaria-blue-2/50" />)}</section>}
        {status === 'error' && <section role="alert" className="rounded-lg bg-red-950/50 p-5 text-red-100">No se pudo cargar el calendario.<button type="button" onClick={() => setRetry((value) => value + 1)} className="ml-3 font-bold underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-notaria-yellow">Reintentar</button></section>}
        {status === 'ready' && (
          <section className="rounded-lg bg-notaria-blue-1/80 p-2 shadow-xl ring-1 ring-white/5 sm:p-4">
            {isEmpty && <p className="mb-3 rounded-md bg-white/5 p-3 text-sm text-gray-300">No hay actividad registrada en este mes.</p>}
            <div role="grid" aria-label={`Calendario de ${monthNames[month.month - 1]} ${month.year}`} className="grid grid-cols-7 gap-1" aria-rowcount={totalSlots / 7} aria-colcount="7">
              {weekDays.map((name) => <div role="columnheader" key={name} className="p-2 text-center text-xs font-bold uppercase tracking-wide text-gray-400">{name}</div>)}
              {Array.from({ length: totalSlots }, (_, index) => {
                const day = index - mondayOffset + 1;
                if (day < 1 || day > daysInMonth) return <div key={`blank-${index}`} role="gridcell" aria-hidden="true" className="min-h-[5.25rem]" />;
                const date = `${month.year}-${String(month.month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
                return <div key={date} role="gridcell"><CalendarDay year={month.year} month={month.month} day={day} entry={daysByDate.get(date)} onOpen={openDay} /></div>;
              })}
            </div>
          </section>
        )}
      </div>
      </main>
      <AnimatePresence>{selectedDay && <DayDrawer day={selectedDay} onClose={closeDrawer} reducedMotion={reducedMotion} />}</AnimatePresence>
    </motion.div>
  );
};

export default AdminCollectionsCalendar;
